/**
 * Fatiado de pages/Menu.tsx por responsabilidade (#131, R3).
 */
import { useState } from 'react'
import type { useQueryClient } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import { categories as catApi, type Category } from '../../lib/api.js'
import { Button, Input, Modal, toast } from '../../components/ui/index.js'
import { useI18n } from '../../lib/i18n.js'
// ─── CategoryModal ────────────────────────────────────────────────────────────

export function CategoryModal({
  open,
  cat,
  locale,
  onClose,
  qc,
}: {
  open: boolean
  cat?: Category
  locale: string
  onClose: () => void
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t } = useI18n()
  const [name, setName] = useState(
    cat?.translations.find((x) => x.locale === locale)?.name ?? cat?.translations[0]?.name ?? '',
  )
  const [order, setOrder] = useState(String(cat?.sort_order ?? 0))

  const mut = useMutation({
    mutationFn: () =>
      cat
        ? catApi.update(cat.id, { name, sort_order: parseInt(order, 10) || 0 })
        : catApi.create({ name, locale, sort_order: parseInt(order, 10) || 0 }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['categories'] })
      toast.success(cat ? t('admin.menu.category_updated') : t('admin.menu.category_created'))
      onClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const deleteMut = useMutation({
    mutationFn: () => catApi.delete(cat!.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['categories'] })
      toast.success(t('admin.menu.category_deactivated'))
      onClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  // Reset ao abrir
  useState(() => {
    if (open) {
      setName(
        cat?.translations.find((x) => x.locale === locale)?.name ??
          cat?.translations[0]?.name ??
          '',
      )
      setOrder(String(cat?.sort_order ?? 0))
    }
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={cat ? t('admin.menu.edit_category') : t('admin.menu.new_category')}
      size="sm"
      footer={
        <div className="flex gap-2 w-full">
          {cat && (
            <Button
              variant="danger"
              size="sm"
              loading={deleteMut.isPending}
              data-testid="catalog-category-deactivate"
              onClick={() => deleteMut.mutate()}
            >
              {t('admin.menu.deactivate')}
            </Button>
          )}
          <div className="flex-1" />
          <Button
            variant="secondary"
            size="sm"
            data-testid="catalog-category-cancel"
            onClick={onClose}
          >
            {t('admin.common.cancel')}
          </Button>
          <Button
            size="sm"
            loading={mut.isPending}
            data-testid="catalog-category-save"
            onClick={() => mut.mutate()}
          >
            {cat ? t('admin.common.save') : t('admin.common.create')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Input
          label={t('admin.menu.name')}
          data-testid="catalog-category-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <Input
          label={t('admin.menu.sort_order')}
          type="number"
          min="0"
          data-testid="catalog-category-order"
          value={order}
          onChange={(e) => setOrder(e.target.value)}
          hint={t('admin.menu.sort_hint')}
        />
      </div>
    </Modal>
  )
}
